import { useEffect, useState } from "react";
import { Heart } from "lucide-react";
import { api } from "@/lib/api";
import { isGuest } from "@/lib/guest";

// Shared set of item ids the current guest/family member has hearted
let mine = null;
let loading = null;
const subs = new Set();
const emit = () => subs.forEach((f) => f(new Set(mine)));
const load = () => (loading ||= api.get("/hearts/mine").then((r) => { mine = new Set(r.data.item_ids); emit(); }).catch(() => { mine = new Set(); }));

export const useHearts = () => {
  const [set, setSet] = useState(mine || new Set());
  useEffect(() => {
    subs.add(setSet);
    if (!mine) load(); else setSet(new Set(mine));
    return () => subs.delete(setSet);
  }, []);
  const toggle = async (id) => {
    const r = (await api.post(`/items/${id}/heart`)).data;
    if (!mine) mine = new Set();
    if (r.hearted) mine.add(id); else mine.delete(id);
    emit();
    return r;
  };
  return { has: (id) => set.has(id), toggle };
};

// Overlay heart (guests + family); family/admin also see the totals
export const HeartButton = ({ it, onChanged }) => {
  const { has, toggle } = useHearts();
  const [counts, setCounts] = useState({ g: it.hearts_guest || 0, f: it.hearts_family || 0 });
  useEffect(() => setCounts({ g: it.hearts_guest || 0, f: it.hearts_family || 0 }), [it.id, it.hearts_guest, it.hearts_family]);
  const on = has(it.id);
  const click = async () => {
    const r = await toggle(it.id);
    setCounts({ g: r.hearts_guest, f: r.hearts_family });
    onChanged?.({ ...it, hearts_guest: r.hearts_guest, hearts_family: r.hearts_family });
  };
  return (
    <div className="flex items-center gap-1.5">
      <button className={`lb-btn ${on ? "heart-on" : ""}`} onClick={click} title={on ? "Unheart" : "Heart this photo"} aria-label="Heart" aria-pressed={on} data-testid="lightbox-heart-button">
        <Heart size={18} fill={on ? "currentColor" : "none"} />
      </button>
      {!isGuest() && (counts.g > 0 || counts.f > 0) && (
        <span className="heart-counts" data-testid="lightbox-heart-counts">
          {counts.g > 0 && <span>♥ {counts.g} guest{counts.g === 1 ? "" : "s"}</span>}
          {counts.f > 0 && <span>♥ {counts.f} family</span>}
        </span>
      )}
    </div>
  );
};

// Grid hover heart (guests)
export const CellHeart = ({ id }) => {
  const { has, toggle } = useHearts();
  const on = has(id);
  const click = (e) => { e.stopPropagation(); toggle(id); };
  return (
    <span role="button" tabIndex={0} className={`cell-heart ${on ? "on" : ""}`} onClick={click} onKeyDown={(e) => e.key === "Enter" && click(e)}
      aria-label="Heart" aria-pressed={on} data-testid={`photo-heart-${id}`}>
      <Heart size={14} fill={on ? "currentColor" : "none"} />
    </span>
  );
};
